import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { oauthAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

export default function ConnectPage() {
  const [searchParams] = useSearchParams();
  const [connections, setConnections] = useState({ microsoft: { connected: false }, google: { connected: false } });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [notification, setNotification] = useState(null);
  const { user } = useAuth();
  const isEmailVerified = user?.emailVerified;

  useEffect(() => {
    loadConnections();

    // Check for OAuth callback status
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const provider = searchParams.get('provider');

    if (success === 'true' && provider) {
      setNotification({
        type: 'success',
        message: `Successfully connected to ${provider === 'microsoft' ? 'Microsoft 365' : 'Google Workspace'}!`,
      });
    } else if (error) {
      setNotification({
        type: 'error',
        message: `Failed to connect: ${error.replace(/_/g, ' ')}`,
      });
    }

    // Reset connecting state when page is restored from bfcache
    // (user clicked back from OAuth provider without completing login)
    const handlePageShow = (event) => {
      if (event.persisted) {
        setConnecting(null);
        loadConnections();
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [searchParams]);

  const loadConnections = async () => {
    try {
      const response = await oauthAPI.getStatus();
      setConnections(response.data);
    } catch (error) {
      console.error('Failed to load connections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (provider) => {
    setConnecting(provider);
    try {
      const response = provider === 'microsoft'
        ? await oauthAPI.getMicrosoftAuthUrl()
        : await oauthAPI.getGoogleAuthUrl();

      // Redirect to OAuth provider
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error(`Failed to initiate ${provider} OAuth:`, error);
      const serverError = error.response?.data?.error;
      const serverMessage = error.response?.data?.message;
      let message;
      if (serverError === 'email_not_verified') {
        message = serverMessage || 'Please verify your email address before connecting a platform. Check your inbox for the verification link.';
      } else {
        message = `Failed to connect to ${provider === 'microsoft' ? 'Microsoft 365' : 'Google Workspace'}. Please try again.`;
      }
      setNotification({
        type: 'error',
        message,
      });
      setConnecting(null);
    }
  };

  const handleDisconnect = async (provider) => {
    if (!window.confirm(`Are you sure you want to disconnect ${provider === 'microsoft' ? 'Microsoft 365' : 'Google Workspace'}?`)) {
      return;
    }

    try {
      await oauthAPI.disconnect(provider);
      setNotification({
        type: 'success',
        message: `Successfully disconnected ${provider === 'microsoft' ? 'Microsoft 365' : 'Google Workspace'}.`,
      });
      loadConnections();
    } catch (error) {
      console.error(`Failed to disconnect ${provider}:`, error);
      setNotification({
        type: 'error',
        message: `Failed to disconnect. Please try again.`,
      });
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Connect Platforms</h1>
        <p className="mt-1 text-slate-500 dark:text-slate-400">
          Connect your Microsoft 365 or Google Workspace to start auditing.
        </p>
      </div>

      {/* Notification */}
      {notification && (
        <div
          className={`rounded-xl p-4 flex items-start gap-3 ${
            notification.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
          }`}
        >
          {notification.type === 'success' ? (
            <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
          ) : (
            <XCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          )}
          <div className="flex-1">
            <p
              className={
                notification.type === 'success'
                  ? 'text-green-700 dark:text-green-300'
                  : 'text-red-700 dark:text-red-300'
              }
            >
              {notification.message}
            </p>
          </div>
          <button
            onClick={() => setNotification(null)}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            <XCircleIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Email Verification Warning */}
      {!isEmailVerified && (
        <div className="rounded-xl p-4 flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <p className="text-amber-700 dark:text-amber-300">
            Please verify your email address before connecting a platform. Check your inbox for the verification link.
          </p>
        </div>
      )}

      {/* Connection Cards */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Microsoft 365 */}
        <Card className="p-8">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/30">
              <svg className="h-8 w-8" viewBox="0 0 23 23">
                <path fill="#f35325" d="M1 1h10v10H1z" />
                <path fill="#81bc06" d="M12 1h10v10H12z" />
                <path fill="#05a6f0" d="M1 12h10v10H1z" />
                <path fill="#ffba08" d="M12 12h10v10H12z" />
              </svg>
            </div>

            <h3 className="mt-4 text-xl font-semibold text-slate-900 dark:text-white">
              Microsoft 365
            </h3>
            <p className="mt-2 text-slate-500 dark:text-slate-400">
              Connect Azure AD, Intune, OneDrive, and SharePoint for security auditing.
            </p>

            {connections.microsoft.connected ? (
              <div className="mt-6 w-full">
                <div className="flex items-center justify-center gap-2 text-green-600 dark:text-green-400 mb-4">
                  <CheckCircleIcon className="h-5 w-5" />
                  <span className="font-medium">Connected</span>
                </div>
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 text-sm">
                  <p className="text-slate-600 dark:text-slate-300">
                    <span className="font-medium">Account:</span> {connections.microsoft.providerEmail}
                  </p>
                  {connections.microsoft.providerDisplayName && (
                    <p className="text-slate-600 dark:text-slate-300 mt-1">
                      <span className="font-medium">Name:</span> {connections.microsoft.providerDisplayName}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  className="mt-4 w-full"
                  onClick={() => handleDisconnect('microsoft')}
                >
                  Disconnect
                </Button>
              </div>
            ) : (
              <Button
                className="mt-6"
                onClick={() => handleConnect('microsoft')}
                loading={connecting === 'microsoft'}
                disabled={!isEmailVerified}
              >
                Connect Microsoft 365
              </Button>
            )}
          </div>

          <div className="mt-8 border-t border-slate-200 dark:border-slate-700 pt-6">
            <h4 className="text-sm font-medium text-slate-900 dark:text-white mb-3">
              What we'll access (read-only):
            </h4>
            <ul className="space-y-2 text-sm text-slate-500 dark:text-slate-400">
              <li>• User profiles and MFA status</li>
              <li>• Directory and organization info</li>
              <li>• Device compliance status</li>
              <li>• Security alerts and risks</li>
            </ul>
          </div>
        </Card>

        {/* Google Workspace */}
        <Card className="p-8">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-red-100 dark:bg-red-900/30">
              <svg className="h-8 w-8" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            </div>

            <h3 className="mt-4 text-xl font-semibold text-slate-900 dark:text-white">
              Google Workspace
            </h3>
            <p className="mt-2 text-slate-500 dark:text-slate-400">
              Connect Google Admin, Drive, and Gmail for security auditing.
            </p>

            {connections.google.connected ? (
              <div className="mt-6 w-full">
                <div className="flex items-center justify-center gap-2 text-green-600 dark:text-green-400 mb-4">
                  <CheckCircleIcon className="h-5 w-5" />
                  <span className="font-medium">Connected</span>
                </div>
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 text-sm">
                  <p className="text-slate-600 dark:text-slate-300">
                    <span className="font-medium">Account:</span> {connections.google.providerEmail}
                  </p>
                  {connections.google.domain && (
                    <p className="text-slate-600 dark:text-slate-300 mt-1">
                      <span className="font-medium">Domain:</span> {connections.google.domain}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  className="mt-4 w-full"
                  onClick={() => handleDisconnect('google')}
                >
                  Disconnect
                </Button>
              </div>
            ) : (
              <Button
                className="mt-6"
                onClick={() => handleConnect('google')}
                loading={connecting === 'google'}
                disabled={!isEmailVerified}
              >
                Connect Google Workspace
              </Button>
            )}
          </div>

          <div className="mt-8 border-t border-slate-200 dark:border-slate-700 pt-6">
            <h4 className="text-sm font-medium text-slate-900 dark:text-white mb-3">
              What we'll access (read-only):
            </h4>
            <ul className="space-y-2 text-sm text-slate-500 dark:text-slate-400">
              <li>• User profiles and 2-step verification</li>
              <li>• Directory and domain info</li>
              <li>• Mobile device inventory</li>
              <li>• Drive sharing settings</li>
            </ul>
          </div>
        </Card>
      </div>

      {/* Info Section */}
      <Card>
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30 flex-shrink-0">
            <CheckCircleIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">Read-Only Access</h3>
            <p className="mt-1 text-slate-500 dark:text-slate-400">
              We only request read-only permissions. Your data is never modified and tokens are encrypted at rest.
              You can disconnect at any time.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
